/*
  Rescue Spa quiz <-> storefront bridge.

  Listens for postMessage events from the quiz results iframe
  (https://quiz.shoprescuespa.com) and:
    • add-to-cart : adds numeric variant ids to the live cart + opens the drawer
    • quick-view  : opens a self-contained overlay built from /products/<h>.js
    • resize      : sets the results iframe height

  When the message carries a `sessionId`, each cart line item is tagged with the
  underscore-prefixed properties `_quiz_session_id` + `_quiz` (hidden from the
  customer, persisted onto the order line item) so the sale can be attributed
  back to the quiz for revenue reporting.

  The quiz sends Shopify NUMERIC variant ids directly, so there is no barcode/
  SKU resolution here (unlike the older skinmatch-cart.js this is based on).
  Contract: QUIZ-SHOPIFY-IFRAME-MASTERPLAN.md §3.
*/
(function () {
  'use strict';

  // Origins allowed to drive the cart. Add the quiz app's production origin and
  // localhost for local `shopify theme dev` testing.
  var ALLOWED_ORIGINS = [
    'https://quiz.shoprescuespa.com',
    'http://localhost:8080',
    'http://127.0.0.1:8080'
  ];
  var IFRAME_ID = 'rescue-quiz-frame';

  function log() {
    if (window.__RESCUE_QUIZ_DEBUG) console.log.apply(console, ['[rescue-quiz]'].concat([].slice.call(arguments)));
  }

  function getCartAddUrl() {
    var base = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';
    return /\.js$/.test(base) ? base : base + '.js';
  }
  function getMiniCart() { return document.querySelector('mini-cart'); }
  function getDrawerSections() {
    var mc = getMiniCart();
    return mc && typeof mc.getSectionsToRender === 'function'
      ? mc.getSectionsToRender().map(function (s) { return s.id; })
      : [];
  }

  // ---- add to cart -------------------------------------------------------

  function postCartAdd(body) {
    return fetch(getCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    });
  }

  // Bulk add is atomic on Shopify's side — one bad item 422s the whole batch.
  function bulkAdd(items) {
    var sections = getDrawerSections();
    var body = { items: items };
    if (sections.length) { body.sections = sections; body.sections_url = window.location.pathname; }
    return postCartAdd(body);
  }

  // Per-item fallback so an out-of-stock item only skips itself.
  function addIndividually(items) {
    var sections = getDrawerSections();
    var failures = [];
    var lastSuccess = null;
    return items.reduce(function (chain, item) {
      return chain.then(function () {
        return postCartAdd({ items: [item] })
          .then(function (res) { lastSuccess = res; })
          .catch(function (err) { failures.push({ id: item.id, error: err }); });
      });
    }, Promise.resolve()).then(function () {
      if (!sections.length) {
        var r0 = lastSuccess || {};
        r0._partialFailures = failures;
        return r0;
      }
      return fetch('/?sections=' + encodeURIComponent(sections.join(',')), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (sectionsJson) {
          var result = lastSuccess || {};
          if (sectionsJson) result.sections = sectionsJson;
          result._partialFailures = failures;
          return result;
        })
        .catch(function () { var r = lastSuccess || {}; r._partialFailures = failures; return r; });
    });
  }

  function addItemsToCart(items) {
    return bulkAdd(items).catch(function (err) {
      log('bulk add failed, falling back to per-item', err);
      return addIndividually(items);
    });
  }

  function refreshDrawer(response) {
    var mc = getMiniCart();
    if (mc && response && response.sections && typeof mc.renderContents === 'function') {
      mc.renderContents(response);
      return true;
    }
    return false;
  }

  function fallbackOpenDrawer() {
    var mc = getMiniCart();
    if (!mc) return Promise.resolve(false);
    return fetch('/?section_id=mini-cart', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return false;
        var container = document.getElementById('mini-cart');
        if (container && typeof mc.getSectionInnerHTML === 'function') {
          container.innerHTML = mc.getSectionInnerHTML(html, '.shopify-section');
        }
        return fetch('/?section_id=cart-icon-bubble', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (bubbleHtml) {
            if (bubbleHtml && typeof mc.getSectionInnerHTML === 'function') {
              ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach(function (id) {
                var el = document.getElementById(id);
                if (el) el.innerHTML = mc.getSectionInnerHTML(bubbleHtml, '.shopify-section');
              });
            }
            if (typeof mc.open === 'function') mc.open();
            return true;
          });
      })
      .catch(function () { return false; });
  }

  function notifyThemeCart(response) {
    // Mirror the theme's product-added event for analytics/observers.
    // Intentionally NOT dispatching `cart:refresh` — cart-options.js listens for
    // it and runs gift-box reconciliation that can reload the page.
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: response } }));
  }

  function handleAddToCart(d) {
    // Attribution tag: underscore-prefixed line-item properties are hidden from
    // the customer in cart/checkout but persist onto the order line item, so the
    // orders webhook / reconciliation can attribute the sale back to the quiz.
    // Only tag when the iframe sent a session id (older embeds omit it).
    var sessionId = d && typeof d.sessionId === 'string' ? d.sessionId.trim() : '';
    var props = sessionId ? { _quiz_session_id: sessionId, _quiz: 'true' } : null;

    var items = (d.items || [])
      .map(function (it) {
        var id = typeof it === 'object' ? it.id : it;
        var qty = (it && it.quantity) || 1;
        var item = { id: id, quantity: qty };
        if (props) item.properties = props;
        return item;
      })
      .filter(function (it) { return /^\d+$/.test(String(it.id)) && Number(it.id) > 0; });

    if (!items.length) { log('add-to-cart: no valid numeric variant ids', d); postToFrame('cart-add-complete'); return; }
    log('add-to-cart', items);

    addItemsToCart(items).then(function (res) {
      var failures = (res && res._partialFailures) || [];
      if (failures.length) notifyUser(items.length - failures.length, items.length, failures);
      var refreshed = refreshDrawer(res);
      notifyThemeCart(res);
      if (!refreshed) return fallbackOpenDrawer();
    }).catch(function (err) {
      notifyUser(0, 0, [], (err && err.description) || 'Could not add products to cart. Please try again.');
    }).then(function () {
      // Tell the iframe the add finished (success or fail) so it can clear its loader.
      postToFrame('cart-add-complete');
    });
  }

  // Notify the results iframe of host-side events. `data` (optional) is merged
  // into the message (e.g. the signed customer context).
  function postToFrame(action, data) {
    var f = document.getElementById(IFRAME_ID);
    if (!f || !f.contentWindow) return;
    var origin = '*';
    try { origin = new URL(f.src).origin; } catch (e) {}
    var msg = { type: 'rescue-quiz-host', action: action };
    if (data) { for (var k in data) { if (Object.prototype.hasOwnProperty.call(data, k)) msg[k] = data[k]; } }
    try { f.contentWindow.postMessage(msg, origin); } catch (e) {}
  }

  // Forward the Liquid-signed logged-in customer context (set on window by
  // sections/quiz-results.liquid) into the iframe, which re-verifies the
  // signature server-side before writing customer metafields. If the shopper is
  // logged out, window.rescueQuizAuth = { loggedIn:false } and the iframe shows a
  // "log in to save" CTA. See QUIZ-SHOPIFY-CUSTOMER-METAFIELD-MASTERPLAN.md.
  function sendCustomerContext() {
    postToFrame('customer-context', window.rescueQuizAuth || { loggedIn: false });
  }

  // ---- quick view --------------------------------------------------------

  // The quick view is built from the product's clean JSON (/products/<h>.js) and
  // rendered with our OWN self-contained styles. We deliberately do NOT scrape the
  // theme's ?view=modal HTML: that markup depends on the product-section grid CSS
  // (which the quiz-results template doesn't load) and on theme CSS variables like
  // --color-background (a bare "r,g,b" triplet that makes `background:var(...)`
  // invalid → a transparent panel). Building from JSON keeps the layout correct and
  // isolated from theme cascade quirks.
  var overlay = null;
  var savedBodyOverflow = '';
  function ensureOverlay() {
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'rescue-quiz-qv';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML =
      '<div class="rescue-quiz-qv__backdrop" data-close></div>' +
      '<div class="rescue-quiz-qv__panel" role="dialog" aria-modal="true" aria-label="Product quick view">' +
        '<button class="rescue-quiz-qv__close" type="button" data-close aria-label="Close">&times;</button>' +
        '<div class="rescue-quiz-qv__content"></div>' +
      '</div>';
    var css = document.createElement('style');
    // !important on structural props so injected/theme stylesheets can't override them.
    css.textContent =
      '.rescue-quiz-qv{position:fixed!important;inset:0!important;z-index:2147483000!important;display:none}' +
      '.rescue-quiz-qv.is-open{display:block!important}' +
      '.rescue-quiz-qv *{box-sizing:border-box}' +
      '.rescue-quiz-qv__backdrop{position:absolute!important;inset:0!important;background:rgba(0,0,0,.5)!important;border:0}' +
      '.rescue-quiz-qv__panel{position:absolute!important;top:0!important;right:0!important;height:100%!important;width:min(460px,94vw)!important;background:#fff!important;color:#1a1b18;box-shadow:-8px 0 32px rgba(0,0,0,.18)!important;overflow-y:auto!important;-webkit-overflow-scrolling:touch;padding:56px 28px 36px!important;font-family:inherit}' +
      '.rescue-quiz-qv__close{position:absolute;top:14px;right:16px;width:38px;height:38px;display:flex;align-items:center;justify-content:center;font-size:24px;line-height:1;background:#fff;border:1px solid #e5e5e0;border-radius:50%;cursor:pointer;color:#1a1b18}' +
      '.rescue-quiz-qv__img{width:100%;aspect-ratio:1/1;object-fit:contain;background:#f4f4f1;margin-bottom:22px}' +
      '.rescue-quiz-qv__vendor{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#8a8a82;margin:0 0 6px}' +
      '.rescue-quiz-qv__title{font-size:22px;font-weight:400;line-height:1.2;margin:0 0 10px}' +
      '.rescue-quiz-qv__price{font-size:18px;margin:0 0 22px}' +
      '.rescue-quiz-qv__price del{color:#a7a79f;margin-right:8px}' +
      '.rescue-quiz-qv__label{display:block;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#6a6a62;margin:0 0 6px}' +
      '.rescue-quiz-qv__select{width:100%;padding:12px;border:1px solid #d8d8d2;background:#fff;color:#1a1b18;font-size:14px;margin-bottom:22px;border-radius:0}' +
      '.rescue-quiz-qv__add{width:100%;padding:16px;background:#1a1b18;color:#fff;border:0;font-size:12px;letter-spacing:.15em;text-transform:uppercase;cursor:pointer}' +
      '.rescue-quiz-qv__add[disabled]{opacity:.55;cursor:default}' +
      '.rescue-quiz-qv__link{display:inline-block;margin-top:16px;font-size:12px;letter-spacing:.1em;text-transform:uppercase;color:#1a1b18}' +
      '.rescue-quiz-qv__desc{margin-top:26px;padding-top:22px;border-top:1px solid #ececE7;font-size:14px;line-height:1.6;color:#4a4a44}' +
      '.rescue-quiz-qv__desc img{max-width:100%;height:auto}' +
      '.rescue-quiz-qv__msg{padding:48px 0;text-align:center;color:#8a8a82}' +
      '.rescue-quiz-qv__spin{display:inline-block;width:14px;height:14px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:rqspin .7s linear infinite;vertical-align:middle;margin-right:8px}' +
      '@keyframes rqspin{to{transform:rotate(360deg)}}';
    document.head.appendChild(css);
    document.body.appendChild(overlay);
    overlay.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-close')) closeQuickView();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay && overlay.classList.contains('is-open')) closeQuickView();
    });
    return overlay;
  }
  function openOverlay(ov) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ov.classList.add('is-open');
    ov.setAttribute('aria-hidden', 'false');
    ov.querySelector('.rescue-quiz-qv__panel').scrollTop = 0;
  }
  function closeQuickView() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.querySelector('.rescue-quiz-qv__content').innerHTML = '';
    document.body.style.overflow = savedBodyOverflow;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) {
    try {
      if (window.Shopify && Shopify.formatMoney && window.theme && theme.moneyFormat) {
        return Shopify.formatMoney(cents, theme.moneyFormat);
      }
    } catch (e) {}
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function openQuickView(handle, variantId, sessionId) {
    if (!handle || !/^[a-z0-9\-_]+$/i.test(handle)) { log('quick-view: bad handle', handle); return; }
    var ov = ensureOverlay();
    var content = ov.querySelector('.rescue-quiz-qv__content');
    content.innerHTML = '<p class="rescue-quiz-qv__msg">Loading…</p>';
    openOverlay(ov);
    fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) { content.innerHTML = '<p class="rescue-quiz-qv__msg">Product unavailable.</p>'; return; }
        renderQuickView(content, p, variantId, sessionId);
      })
      .catch(function () {
        content.innerHTML = '<p class="rescue-quiz-qv__msg">Could not load this product.</p>';
      });
  }

  function renderQuickView(content, p, variantId, sessionId) {
    var qvSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    var img = p.featured_image || (p.images && p.images[0]) || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    var variants = (p.variants || []).filter(Boolean);
    // Prefer the variant the customer selected on the card; else first available.
    var current = (variantId != null && variants.filter(function (v) { return String(v.id) === String(variantId); })[0]) ||
      variants.filter(function (v) { return v.available; })[0] || variants[0] || { id: null, price: p.price };
    var optName = (p.options && p.options[0] && (p.options[0].name || p.options[0])) || 'Option';

    var selectHtml = '';
    if (variants.length > 1) {
      selectHtml = '<label class="rescue-quiz-qv__label">' + esc(optName) + '</label>' +
        '<select class="rescue-quiz-qv__select">' +
        variants.map(function (v) {
          return '<option value="' + v.id + '" data-price="' + v.price + '"' +
            (v.id === current.id ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' +
            esc(v.public_title || v.title) + (v.available ? '' : ' — sold out') + '</option>';
        }).join('') + '</select>';
    }
    var compare = p.compare_at_price && p.compare_at_price > current.price
      ? '<del>' + money(p.compare_at_price) + '</del>' : '';

    var wrap = document.createElement('div');
    wrap.innerHTML =
      (img ? '<img class="rescue-quiz-qv__img" src="' + esc(img) + '" alt="' + esc(p.title) + '">' : '') +
      (p.vendor ? '<p class="rescue-quiz-qv__vendor">' + esc(p.vendor) + '</p>' : '') +
      '<h2 class="rescue-quiz-qv__title">' + esc(p.title) + '</h2>' +
      '<div class="rescue-quiz-qv__price">' + compare +
        '<span class="rescue-quiz-qv__price-now">' + money(current.price) + '</span></div>' +
      selectHtml +
      '<button class="rescue-quiz-qv__add" type="button"' + (current.available ? '' : ' disabled') + '>' +
        (current.available ? 'Add to cart' : 'Sold out') + '</button>' +
      '<a class="rescue-quiz-qv__link" href="' + esc(p.url) + '" target="_top">View full details &rarr;</a>' +
      '<div class="rescue-quiz-qv__desc"></div>';
    // Description is trusted first-party store HTML.
    wrap.querySelector('.rescue-quiz-qv__desc').innerHTML = p.description || '';
    content.innerHTML = '';
    content.appendChild(wrap);

    var sel = wrap.querySelector('.rescue-quiz-qv__select');
    var priceNow = wrap.querySelector('.rescue-quiz-qv__price-now');
    var addBtn = wrap.querySelector('.rescue-quiz-qv__add');
    var selectedId = current.id;
    if (sel) {
      sel.addEventListener('change', function () {
        var o = sel.options[sel.selectedIndex];
        selectedId = o.value;
        priceNow.textContent = money(parseInt(o.getAttribute('data-price'), 10));
        var avail = !o.disabled;
        addBtn.disabled = !avail;
        addBtn.textContent = avail ? 'Add to cart' : 'Sold out';
      });
    }
    addBtn.addEventListener('click', function () {
      if (!selectedId || addBtn.disabled) return;
      addBtn.disabled = true;
      addBtn.innerHTML = '<span class="rescue-quiz-qv__spin"></span>Adding…';
      var qvItem = { id: selectedId, quantity: 1 };
      if (qvSessionId) qvItem.properties = { _quiz_session_id: qvSessionId, _quiz: 'true' };
      addItemsToCart([qvItem]).then(function (res) {
        var refreshed = refreshDrawer(res);
        notifyThemeCart(res);
        closeQuickView();
        if (!refreshed) return fallbackOpenDrawer();
      }).catch(function (err) {
        notifyUser(0, 0, [], (err && err.description) || 'Could not add to cart. Please try again.');
        addBtn.disabled = false;
        addBtn.textContent = 'Add to cart';
      });
    });
  }

  // ---- resize ------------------------------------------------------------

  function handleResize(height) {
    var frame = document.getElementById(IFRAME_ID);
    if (!frame) return;
    var h = Math.max(300, Math.min(20000, parseInt(height, 10) || 0));
    if (h) frame.style.height = h + 'px';
  }

  // ---- modal bridge (consultation scheduler) -----------------------------
  // The scheduler dialog is `position:fixed;top:50%` inside the results iframe, so
  // it sits at the iframe's content-center — far below the store's fold, and the
  // store page (not the iframe) is what scrolls. Resizing the iframe does NOT
  // re-anchor an already-open fixed dialog, so instead, when the iframe reports a
  // modal is open, we scroll the store page so the iframe's content-center lands in
  // the viewport, then lock scrolling so the page can't move behind the dialog.
  // On close we unlock and restore the previous scroll position.
  var modalOpen = false;
  var savedDocOverflow = '';
  var savedModalBodyOverflow = '';
  var savedScrollY = 0;
  function preventScroll(e) { e.preventDefault(); }

  function handleModalOpen() {
    var frame = document.getElementById(IFRAME_ID);
    if (!frame || modalOpen) return;
    modalOpen = true;
    savedDocOverflow = document.documentElement.style.overflow;
    savedModalBodyOverflow = document.body.style.overflow;
    savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var docTop = frame.getBoundingClientRect().top + savedScrollY;
    var target = Math.max(0, Math.round(docTop + frame.offsetHeight / 2 - window.innerHeight / 2));
    window.scrollTo(0, target);
    // Lock the store page: overflow:hidden plus a non-passive wheel/touch blocker
    // (overflow alone doesn't reliably stop momentum/programmatic scroll on all
    // themes). Wheel/touch inside the cross-origin dialog stays inside its own
    // frame, so the scheduler still scrolls.
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });
  }

  function handleModalClose() {
    if (!modalOpen) return;
    modalOpen = false;
    window.removeEventListener('wheel', preventScroll, { passive: false });
    window.removeEventListener('touchmove', preventScroll, { passive: false });
    document.documentElement.style.overflow = savedDocOverflow;
    document.body.style.overflow = savedModalBodyOverflow;
    window.scrollTo(0, savedScrollY);
  }

  // ---- toast (partial-failure feedback) ----------------------------------

  function notifyUser(added, total, failures, errorMessage) {
    var msg;
    if (errorMessage) msg = errorMessage;
    else if (added === 0) msg = 'No products were added to your cart. Please try again.';
    else if (failures && failures.length) msg = 'Added ' + added + ' of ' + total + ' products. ' + failures.length + ' could not be added (some may be out of stock).';
    else return;
    var toast = document.createElement('div');
    toast.setAttribute('role', 'status');
    toast.textContent = msg;
    toast.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#1a1b18;color:#fff;padding:14px 22px;border-radius:4px;font-size:14px;z-index:100001;box-shadow:0 4px 20px rgba(0,0,0,.15);max-width:90vw;text-align:center';
    document.body.appendChild(toast);
    setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 5000);
  }

  // ---- message router ----------------------------------------------------

  window.addEventListener('message', function (e) {
    if (ALLOWED_ORIGINS.indexOf(e.origin) === -1) return;
    var d = e.data;
    if (!d || typeof d !== 'object' || d.type !== 'rescue-quiz') return;
    log('message', d);
    switch (d.action) {
      case 'add-to-cart': handleAddToCart(d); break;
      case 'quick-view': openQuickView(d.handle, d.variantId, d.sessionId); break;
      case 'resize': handleResize(d.height); break;
      case 'modal-open': handleModalOpen(); break;
      case 'modal-close': handleModalClose(); break;
      // When the iframe is ready / results are shown, hand it the signed
      // customer context so it can sync recommendations to the account.
      case 'ready': sendCustomerContext(); break;
      case 'results-ready': sendCustomerContext(); break;
    }
  });
})();
